/**
 * Tab-capture for the scenarios POC.
 *
 * Browsers cannot silently record a page (Mapbox WebGL + DOM) without either:
 *   1) getDisplayMedia (user picks this tab once), or
 *   2) an external driver (Playwright/Puppeteer) with video recording.
 *
 * Prefers MP4/H.264 when supported (Chromium/Arc/Safari); WebM fallback.
 */

export type PreviewRecorder = {
  stop: () => Promise<Blob>
  stream: MediaStream
  mimeType: string
  extension: 'mp4' | 'webm'
}

function pickRecorderFormat(): { mimeType?: string; extension: 'mp4' | 'webm' } {
  const mp4 = [
    'video/mp4;codecs=avc1.640028',
    'video/mp4;codecs=avc1.4D4028',
    'video/mp4;codecs=avc1.42E01E',
    'video/mp4;codecs=avc1',
    'video/mp4',
  ]
  for (const type of mp4) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
      return { mimeType: type, extension: 'mp4' }
    }
  }
  const webm = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm']
  for (const type of webm) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
      return { mimeType: type, extension: 'webm' }
    }
  }
  return { extension: 'webm' }
}

function idealCaptureSize(): { width: number; height: number } {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const width = Math.round(Math.max(window.screen.width, window.innerWidth) * dpr)
  const height = Math.round(Math.max(window.screen.height, window.innerHeight) * dpr)
  return {
    width: Math.min(Math.max(width, 1920), 3840),
    height: Math.min(Math.max(height, 1080), 2160),
  }
}

/** Prompt the user to share this browser tab (Chromium: prefer current tab). */
export async function requestTabCapture(): Promise<MediaStream | null> {
  if (!navigator.mediaDevices?.getDisplayMedia) return null
  const { width, height } = idealCaptureSize()
  try {
    const opts = {
      video: {
        frameRate: { ideal: 30, max: 60 },
        displaySurface: 'browser',
        width: { ideal: width, max: 3840 },
        height: { ideal: height, max: 2160 },
      },
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
      monitorTypeSurfaces: 'exclude',
    }
    const withAudio = { ...opts, audio: true, video: opts.video } as DisplayMediaStreamOptions
    const silent = { ...opts, audio: false, video: opts.video } as DisplayMediaStreamOptions
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getDisplayMedia(withAudio)
    } catch (err) {
      const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: unknown }).name) : ''
      if (name === 'NotAllowedError' || name === 'AbortError') return null
      stream = await navigator.mediaDevices.getDisplayMedia(silent)
    }
    const track = stream.getVideoTracks()[0]
    if (track) {
      try {
        await track.applyConstraints({
          width: { ideal: width },
          height: { ideal: height },
          frameRate: { ideal: 30, max: 60 },
        })
      } catch {
        /* browser may ignore; capture still works at default size */
      }
    }
    return stream
  } catch {
    return null
  }
}

export function startPreviewRecorder(stream: MediaStream): PreviewRecorder {
  const chunks: Blob[] = []
  const { mimeType, extension } = pickRecorderFormat()
  const recorder = new MediaRecorder(stream, {
    mimeType,
    // Higher bitrate keeps Timeline / UI text sharper than the default ~2.5 Mbps.
    videoBitsPerSecond: 20_000_000,
  })
  const resolvedType = recorder.mimeType || mimeType || (extension === 'mp4' ? 'video/mp4' : 'video/webm')
  const isMp4 = resolvedType.includes('mp4')
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data)
  }
  // Chrome/Arc MP4 muxer breaks with 1s timeslices (~30s truncated playback). One segment only.
  if (isMp4) recorder.start()
  else recorder.start(1000)

  return {
    stream,
    mimeType: resolvedType,
    extension: isMp4 ? 'mp4' : extension,
    stop: () =>
      new Promise<Blob>((resolve) => {
        const finish = () => {
          stream.getTracks().forEach((t) => t.stop())
          resolve(new Blob(chunks, { type: resolvedType }))
        }
        if (recorder.state === 'inactive') {
          finish()
          return
        }
        recorder.addEventListener('stop', finish, { once: true })
        try {
          if (recorder.state === 'recording') recorder.requestData()
        } catch {
          /* ignore */
        }
        recorder.stop()
      }),
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 2_000)
}

export function previewVideoFilename(
  title: string,
  durationMs: number,
  extension: 'mp4' | 'webm' = 'mp4',
): string {
  const slug =
    title
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48) || 'capture'
  const sec = Math.max(1, Math.round(durationMs / 1000))
  return `${slug}-capture-${sec}s.${extension}`
}
